/* eslint-disable max-lines */
import { bannerMsgR2 } from 'services/graphql/fragments/BannerMessagesR2';
import { gql } from 'urql';

import {
  AnalyticsFragment,
  FullWidthTextComponentTileFragment,
  IconTitleLabelRowTile,
  SpacingComponentTileFragment,
  VxlCardContainer,
  VxlPageSectionVerticalStackContainer,
  VxlVerticallySplitTile,
  VxlVerticalSegmentedCardContainer,
} from './VxlFragments';
import {
  IconFragment,
  VXLResponsiveTextFragment,
  VXLTextFragment,
} from './VxlStyles';

export const AccountDetailItem = gql`
  fragment accountDetailItem on AccountDetailItem {
    key
    title
    value
    description
    articleLink {
      copy
      articleId
      article {
        id
        title
      }
    }
    itemDetail {
      title
      description
      descriptionHtml
    }
  }
`;

export const AccountDetailGroup = gql`
  fragment accountDetailGroup on AccountDetailGroup {
    ... on AccountDetailBalanceGroup {
      title
      items {
        ...accountDetailItem
      }
    }
    ... on AccountDetailCopyItemGroup {
      title
      items {
        ...accountDetailItem
      }
    }
    ... on AccountDetailInterestGroup {
      title
      items {
        ...accountDetailItem
      }
    }
    ... on AccountDetailMoneyItemGroup {
      title
      items {
        ...accountDetailItem
      }
    }
    ... on AccountDetailWithdrawalGroup {
      title
      items {
        ...accountDetailItem
      }
    }
    ... on ChargeCardAccountDetailBalanceGroup {
      title
      items {
        ...accountDetailItem
      }
    }
    ... on ChargeCardAccountDetailPaymentGroup {
      title
      items {
        ...accountDetailItem
      }
    }
    ... on LoanAccountDetailAmountGroup {
      title
      items {
        ...accountDetailItem
      }
    }
    ... on LoanAccountDetailInformationGroup {
      title
      items {
        ...accountDetailItem
      }
    }
    ... on LoanAccountDetailPaymentGroup {
      title
      items {
        ...accountDetailItem
      }
    }
  }
  ${AccountDetailItem}
`;

export const associatedAccounts = gql`
  fragment associatedAccounts on Account {
    id
    type
    status
    name
    nickname
    iconUrl
    isDefaultAccount
    __typename
  }
`;

export const tag = gql`
  fragment tag on Tag {
    id
    tagColor
    icon
    text
    __typename
  }
`;

export const articleLink = gql`
  fragment articleLink on ArticleLink {
    copy
    articleId
    article {
      id
      title
    }
    __typename
  }
`;

export const creditScore = gql`
  fragment creditScore on InternalChargeCardAccount {
    creditScore {
      __typename
      ... on ICreditScore {
        score
        grade
        description
        ranges {
          name
          description
          min
          max
        }
      }
      ... on CreditScoreSuccess {
        gradeEnum
        scoreDelta
        providerLogo
        lastUpdated
        factors {
          name
          value
        }
      }
    }
  }
`;

export const fullName = gql`
  fragment fullName on FullName {
    first
    full
    last
    initials
    __typename
  }
`;

export const institution = gql`
  fragment institution on Institution {
    name
    __typename
  }
`;

export const legalDocument = gql`
  fragment legalDocument on LegalDocument {
    name
    url
    format
    __typename
  }
`;

export const money = gql`
  fragment money on Money {
    amount
    currency
    formatted
    formattedWithPlusPrefix
    formattedWithPlusPrefixOrAbs
    __typename
  }
`;

export const pageInfo = gql`
  fragment pageInfo on PageInfo {
    __typename
    endCursor
    hasNextPage
  }
`;

export const transactionConnection = gql`
  fragment transactionConnection on TransactionConnection {
    edges {
      node {
        id
        description
        shortDescription
        direction
        value {
          ...money
        }
        ... on IBankingTransaction {
          runningBalance {
            ...money
          }
        }
        type
        status
        createdAt
        category {
          name
        }
      }
    }
    pageInfo {
      ...pageInfo
    }
  }
  ${money}
  ${pageInfo}
`;

export const transactionConnectionR2 = gql`
  fragment transactionConnectionR2 on TransactionConnectionR2 {
    edges {
      node {
        id
        description
        shortDescription
        direction
        value {
          ...money
        }
        ... on IBankingTransaction {
          runningBalance {
            ...money
          }
        }
        type
        status
        createdAt
        canSeeDetails
        category {
          name
        }
        ... on RdcTransaction {
          rdcId
        }
        ... on ChargeCardTransaction {
          merchant {
            name
          }
        }
        caption
      }
    }
    pageInfo {
      ...pageInfo
    }
  }
  ${money}
  ${pageInfo}
`;

export const statement = gql`
  fragment statement on Statement {
    id
    title
    accountId
    documentId
    dateCopy
    type
    month
    year
    __typename
  }
`;

export const action = gql`
  fragment action on TransferInfoAction {
    type
    title
    cta
    message
  }
`;

export const transferInfo = gql`
  fragment transferInfo on TransferInfo {
    selectable
    message
    action {
      ...action
    }
    __typename
  }
  ${action}
`;

export const statementGroup = gql`
  fragment statementGroup on StatementGroup {
    id
    title
    type
    year
    statements {
      ...statement
    }
    groups {
      title
      type
      year
      month
      statements {
        ...statement
      }
      __typename
    }
    __typename
  }
  ${statement}
`;

export const IllustrationStyleDictionary = gql`
  fragment IllustrationStyleDictionaryFragment on IllustrationStyleDictionary {
    __typename
    id
    illustrationKey
    fallbackURL
  }
`;

export const card = gql`
  fragment card on Card {
    id
    type
    status
    maskedCardNumber
    name
    isLocked
    isPinSet
    isDigital
    isPhysical
    digital
    actions
    isReplaceable
    changePinOptions
    thumbnail {
      __typename
      ...IllustrationStyleDictionaryFragment
    }
    illustration {
      __typename
      ...IllustrationStyleDictionaryFragment
    }
    associatedAccountIds
    cardHolderName {
      full
    }
    associatedAccounts {
      ...associatedAccounts
    }
    shippingInformation: shippingInformationV2 {
      arrivalMessage
    }
    __typename
  }
  ${associatedAccounts}
  ${IllustrationStyleDictionary}
`;

export const activeAdvance = gql`
  fragment activeAdvance on InternalAdvance {
    id
    description
    dateOfAdvance
    dueDate
    amountDue {
      ...money
    }
    canRepay
  }
  ${money}
`;

export const agreements = gql`
  fragment agreements on AccountAgreement {
    id
    title
    documentId
  }
`;

export const details = gql`
  fragment details on AccountDetailGroup {
    title
    items {
      key
      description
      title
      value
      __typename
    }
    __typename
  }
`;

export const displayBalance = gql`
  fragment displayBalance on TitledAmount {
    amount {
      ...money
    }
    title
    __typename
  }
  ${money}
`;

export const account = gql`
  fragment account on Account {
    id
    apy
    yearToDateInterest {
      ...money
    }
    institution {
      ...institution
    }
    type
    product
    status
    name
    nickname
    iconUrl
    previewBalance
    isDefaultAccount
    accountNumber
    accountNumberLast4
    routingNumber
    totalBalance {
      ...money
    }
    availableBalance {
      ...money
    }
    currentBalance {
      ...money
    }
    cards {
      ...card
    }
    transferInfo {
      ...transferInfo
    }
    details {
      ...details
    }
    tag {
      ...tag
    }
    __typename
  }
  ${card}
  ${details}
  ${institution}
  ${money}
  ${transferInfo}
  ${tag}
`;

export const internalAccount = gql`
  fragment internalAccount on IInternalAccount {
    id
    institution {
      ...institution
    }
    type
    product
    status
    name
    nickname
    iconUrl
    accountNumber
    accountNumberLast4
    tag {
      ...tag
    }
    details {
      ...details
    }
    ... on InternalCheckingAccount {
      isDefaultAccount
      routingNumber
      availableBalance {
        ...money
      }
      displayBalance {
        ...displayBalance
      }
      cards {
        ...card
      }
    }
    ... on InternalChargeCardAccount {
      availableBalance {
        ...money
      }
      displayBalance {
        ...displayBalance
      }
      totalBalance {
        ...money
      }
      cards {
        ...card
      }
    }
    ... on InternalPersonalLoanAccount {
      displayBalance {
        ...displayBalance
      }
    }
    ... on InternalSavingsAccount {
      apy
      availableBalance {
        ...money
      }
      displayBalance {
        ...displayBalance
      }
      yearToDateInterest {
        ...money
      }
    }
    tag {
      ...tag
    }
    __typename
  }
  ${card}
  ${details}
  ${displayBalance}
  ${institution}
  ${money}
  ${tag}
`;

export const externalAccount = gql`
  fragment externalAccount on IExternalAccount {
    id
    institution {
      ...institution
    }
    type
    product
    status
    name
    nickname
    iconUrl
    accountNumber
    accountNumberLast4
    ... on ExternalAccount {
      isDefaultAccount
      routingNumber
      transferInfo {
        ...transferInfo
      }
      details {
        ...details
      }
      tag {
        ...tag
      }
    }
    ... on ExternalPlaidAccount {
      isDefaultAccount
      lastUpdated
      lastUpdatedCopy
      accountTypeLast2
      displayBalance {
        ...displayBalance
      }
      routingNumber
      transferInfo {
        ...transferInfo
      }
      details {
        ...details
      }
      tag {
        ...tag
      }
    }

    __typename
  }
  ${details}
  ${institution}
  ${tag}
  ${transferInfo}
  ${displayBalance}
`;

export const ExternalPlaidAccount = gql`
  fragment externalPlaidAccount on ExternalPlaidAccount {
    id
    institution {
      name
    }
    displayBalance {
      ...displayBalance
    }
    tag {
      ...tag
    }
    iconUrl
    lastUpdatedCopy
    accountTypeLast2
  }
  ${displayBalance}
  ${tag}
`;

export const ExternalAccountsTile = gql`
  fragment externalAccountsTile on ExternalAccountsTile {
    id
    externalAccounts {
      ...externalPlaidAccount
    }
  }
  ${ExternalPlaidAccount}
`;

export const accountWithStatements = gql`
  fragment accountWithStatements on Account {
    id
    apy
    yearToDateInterest {
      ...money
    }
    institution {
      ...institution
    }
    type
    product
    status
    name
    nickname
    iconUrl
    createdAt
    isDefaultAccount
    accountNumber
    routingNumber
    totalBalance {
      ...money
    }
    availableBalance {
      ...money
    }
    cards {
      ...card
    }
    transferInfo {
      ...transferInfo
    }
    details {
      title
      items {
        key
        description
        title
        value
        articleLink {
          copy
          articleId
          article {
            id
            title
          }
        }
        __typename
      }
      __typename
    }
    monthlyStatements {
      ...statementGroup
    }
    __typename
  }

  ${card}
  ${displayBalance}
  ${institution}
  ${money}
  ${statementGroup}
  ${transferInfo}
`;

export const address = gql`
  fragment address on Address {
    addressLine1
    addressLine2
    city
    state
    country
    zip
  }
`;

export const coordinate = gql`
  fragment coordinate on GeoCoordinate {
    latitude
    longitude
  }
`;

export const DepositLocation = gql`
  fragment DepositLocation on DepositLocation {
    id
    name
    type
    phoneNumber
    coordinate {
      ...coordinate
    }
    address {
      ...address
    }
    distance
    isFreeCashDepositLocation
    isBarcodeFreeDepositLocation
    customerFee
    retailerKey
    __typename
  }
  ${coordinate}
  ${address}
`;

export const WithdrawalLocation = gql`
  fragment WithdrawalLocation on WithdrawalLocation {
    id
    name
    type
    coordinate {
      ...coordinate
    }
    address {
      ...address
    }
    distance
  }
  ${coordinate}
  ${address}
`;

export const error = gql`
  fragment error on Error {
    code
    title
    message
  }
`;

export const InternalAccountNumber = gql`
  fragment InternalAccountNumber on IInternalBankingAccount {
    id
    product
    name
    nickname
    accountNumberLast4
    fullAccountNumber
    routingNumber
  }
`;

export const disputeInfo = gql`
  fragment disputeInfo on DisputeInfo {
    id
    transactionType
    disputeCategory
    estimatedDisputeResolutionDate
    reasonCategoryMap {
      optionTitle
      disputeCategory
      isReasonCommon
      consideration {
        title
        message
      }
    }
    questionnaireResponses {
      question
      answer
    }
  }
`;

export const MoveMoneyTile = gql`
  fragment moveMoneyTile on MoveMoneyTile {
    id
    status
    title
    __typename
  }
`;

export const PartnerOfferTile = gql`
  fragment partnerOfferTile on PartnerOfferTile {
    id
    status
    tileType
    partnerOfferId
    title
    cta
    description
    icon
    rightImage
    __typename
  }
`;

export const CardTrackerTile = gql`
  fragment cardTrackerTile on CardTrackerTile {
    id
    status
    tileType
    cardTrackerTileType
    card {
      id
      maskedCardNumber
      status
      shippingInformationV2 {
        arrivalMessage
      }
      actions
    }
    __typename
  }
`;

export const wizardAction = gql`
  fragment wizardAction on WizardAction {
    __typename
    id
    copy
    name
    action
    type
    analyticsEvent {
      ...analytics
    }
  }
  ${AnalyticsFragment}
`;

export const accountAction = gql`
  fragment accountAction on AccountAction {
    __typename
    id
    copy
    accountId
    action
    type
    analyticsEvent {
      ...analytics
    }
  }
  ${AnalyticsFragment}
`;

export const screenAction = gql`
  fragment screenAction on Action {
    id
    copy
    action
    link
    type
    analyticsEvent {
      ...analytics
    }
  }
  ${AnalyticsFragment}
`;

export const iconStyleDictionary = gql`
  fragment iconStyleDictionary on IconStyleDictionary {
    __typename
    id
    fallbackURL
    iconKey
  }
`;

export const steppedProgressBarTile = gql`
  fragment steppedProgressBarTile on SteppedProgressBarTile {
    __typename
    id
    status
    tileType
    totalBreakPoints
    progressedPoints
    leadingAccessory {
      __typename
      id
      title
      valueText
      isDisabled
      icon {
        ...iconStyleDictionary
      }
    }
    trailingAccessory {
      __typename
      id
      title
      valueText
      isDisabled
      icon {
        ...iconStyleDictionary
      }
    }
  }
  ${iconStyleDictionary}
`;

export const ctaActionTile = gql`
  fragment ctaActionTile on CtaActionTile {
    __typename
    id
    status
    tileType
    action {
      __typename
      ...wizardAction
      ...accountAction
      ...screenAction
    }
  }
  ${wizardAction}
  ${accountAction}
  ${screenAction}
`;

export const ProgressTrackerTile = gql`
  fragment progressTrackerTile on ProgressTrackerTile {
    id
    status
    tileType
    progressTrackerTileType
    title
    progressCopy
    progressPercentage
    numerator
    denominator
    displayAnalytics {
      ...analytics
    }
    onClickAnalytics {
      ...analytics
    }
  }
  ${AnalyticsFragment}
`;

export const InternalAccountTile = gql`
  fragment internalAccountTile on InternalAccountTile {
    internalAccountTileType
    accountId
    account {
      id
      product
      nickname
      accountNumberLast4
      tag {
        ...tag
      }
      ... on IDisplayBalance {
        displayBalance {
          amount {
            amount
            formatted
          }
          title
        }
      }
    }
    tiles {
      __typename
      ... on ListTile {
        tiles {
          __typename
          id
          ... on AccountActionTile {
            accountActionTileType
            ctaLabel: cta
            icon
            id
            status
            cardId
            tags {
              ...tag
            }
            analyticsEvent {
              ...analytics
            }
          }
        }
        listTileType
        listTitle
      }
      ... on AccountActionListTile {
        tiles {
          __typename
          id
          ... on AccountActionTile {
            accountActionTileType
            ctaLabel: cta
            icon
            id
            status
            tags {
              ...tag
            }
            analyticsEvent {
              ...analytics
            }
          }
        }
        title
        tileType
      }
      ... on CardTrackerAttachmentTile {
        cardId
        id
        status
        tileType
        cardTrackerAttachmentTileType
        title
        card {
          id
          type
          isLocked
          status
          isPinSet
          digital
          shippingInformationV2 {
            arrivalMessage
          }
          actions
        }
      }
      ...progressTrackerTile
      ...ctaActionTile
      ...steppedProgressBarTile
    }
  }
  ${tag}
  ${AnalyticsFragment}
  ${steppedProgressBarTile}
  ${ctaActionTile}
  ${ProgressTrackerTile}
`;

export const ProductOfferTile = gql`
  fragment productOfferTile on ProductOfferTile {
    productOfferTileType
    title
    description
    cta
    application {
      id
      ... on AdvanceApplication {
        advanceApplicationStatus
      }
      ... on ChargeCardApplication {
        chargeCardApplicationStatus
      }
    }
  }
`;

export const cardAction = gql`
  fragment cardAction on CardAction {
    id
    copy
    action
    link
    type
    card {
      ...card
    }
    analyticsEvent {
      ...analytics
    }
  }
  ${AnalyticsFragment}
  ${card}
`;

export const ButtonTile = gql`
  fragment buttonTile on ButtonTile {
    ctaLabel: cta
    buttonTileType
    analytics {
      ...analytics
    }
  }
  ${AnalyticsFragment}
`;

export const PromotionTile = gql`
  fragment promotionTile on PromotionTile {
    promotionTileType
    title
    description
    icon
    cta
    dismissToken
    analytics {
      ...analytics
    }
  }
  ${AnalyticsFragment}
`;

export const GenericTile = gql`
  fragment genericTile on ITile {
    __typename
    id
    tileType
    status
  }
`;

export const PlaidAccountTile = gql`
  fragment plaidAccountTile on PlaidAccountTile {
    accountId
    institution {
      name
    }
    iconUrl
    balance {
      amount
    }
    tag {
      ...tag
    }
    accountTypeLast2
    lastUpdated
  }
  ${tag}
`;

export const ListItemTile = gql`
  fragment listItemTile on ListItemTile {
    id
    title
  }
`;

export const LinkedAccountsTile = gql`
  fragment linkedAccountsTile on LinkedAccountsTile {
    title
    analytics {
      ...analytics
    }
    tiles {
      id
      ...genericTile
      ...externalAccountsTile
      ...listItemTile
    }
  }
  ${AnalyticsFragment}
  ${GenericTile}
  ${ListItemTile}
  ${ExternalAccountsTile}
`;

export const NextBestActionTile = gql`
  fragment nextBestActionTile on NextBestActionTile {
    __typename
    id
    status
    tileType
    carousel {
      __typename
      ... on NurtureAction {
        id
        title
        image
        description
        actionV2 {
          ...cardAction
          ...screenAction
        }
        expressiveColor
        displayAnalytics {
          ...analytics
        }
      }
    }
    nurtureTitle: title
    layout
  }
  ${cardAction}
  ${screenAction}
  ${AnalyticsFragment}
`;

export const CreditScoreTile = gql`
  fragment creditScoreTile on CreditScoreTile {
    id
    tileId
    status
    creditScoreNudgeMessage {
      title
      message
      isActionable
      nudgeMessageType
      displayAnalytics {
        name
        properties {
          key
          value
        }
      }
      clickAnalytics {
        name
        properties {
          key
          value
        }
      }
    }
    creditScore {
      ... on CreditScoreSuccess {
        description
        score
        scoreDelta
        lastUpdated
        grade
        gradeEnum
        factors {
          name
          value
        }
      }
      ... on CreditScoreNoScore {
        description
        grade
        gradeEnum
        ranges {
          description
          max
          min
          name
        }
        score
      }
    }
    tileType
    __typename
  }
`;

export const VaroToAnyoneTile = gql`
  fragment varoToAnyoneTile on VaroToAnyoneTile {
    __typename
    id
    topRecipients {
      recipient {
        id
        email {
          address
        }
        phoneNumber
        displayName
        ... on V2AInternalRecipient {
          name {
            initials
            full
            first
            last
          }
        }
      }
      analyticsEvent {
        ...analytics
      }
    }
    tileType
    __typename
  }
`;

export const AdvanceOfferTile = gql`
  fragment offerAdvanceTile on OfferAdvanceTile {
    __typename
    id
    status
    tileType
    title
    balanceCopy
    chevronAction {
      __typename
      ...wizardAction
      ...accountAction
      ...screenAction
    }
    displayAnalytics {
      ...analytics
    }
    tiles {
      __typename
      id
      ...steppedProgressBarTile
      ...fullWidthTextComponentTileFragment
      ...ctaActionTile
    }
  }
  ${AnalyticsFragment}
  ${wizardAction}
  ${accountAction}
  ${screenAction}
  ${steppedProgressBarTile}
  ${ctaActionTile}
  ${FullWidthTextComponentTileFragment}
`;

const VxlTaggedTextComponentTileFragment = gql`
  fragment vxlTaggedTextComponentTileFragment on VXLTaggedTextComponentTile {
    spacing
    tag {
      ...tag
    }
    text {
      ...VXLTextFragment
    }
  }
  ${tag}
  ${VXLTextFragment}
`;

const SdListTile = gql`
  fragment sdListTile on SDListTile {
    __typename
    id
    status
    tileType
    tiles {
      __typename
      id
      status
      tileType
      ...fullWidthTextComponentTileFragment
      ...vxlTaggedTextComponentTileFragment
      ...iconTitleLabelRowTileFragment
      ...spacingComponentTileFragment
      ...vxlButtonTileFragment
      ...vxlVerticallySplitTileFragment
    }
    displayAnalytics {
      __typename
      name
      properties {
        __typename
        key
        value
      }
    }
  }
  ${FullWidthTextComponentTileFragment}
  ${VxlTaggedTextComponentTileFragment}
  ${IconTitleLabelRowTile}
  ${SpacingComponentTileFragment}
  ${VxlVerticallySplitTile}
`;

export const TileServiceTile = gql`
  fragment TileServiceTileFragment on TileServiceTile {
    id
    tileType
    status
    container {
      __typename
      ...VXLVerticalSegmentedCardContainer
      ...VXLCardContainerFragment
      ...VXLPageSectionVerticalStackContainerFragment
    }
  }
  ${VxlVerticalSegmentedCardContainer}
  ${VxlCardContainer}
  ${VxlPageSectionVerticalStackContainer}
`;

export const LendingServiceTile = gql`
  fragment LendingServiceTileFragment on LendingServiceTile {
    id
    tileType
    status
    tileId
    container {
      __typename
      ...VXLVerticalSegmentedCardContainer
      ...VXLCardContainerFragment
      ...VXLPageSectionVerticalStackContainerFragment
    }
  }
  ${VxlVerticalSegmentedCardContainer}
  ${VxlCardContainer}
  ${VxlPageSectionVerticalStackContainer}
`;

export const SdVerticalStackContainerTile = gql`
  fragment SDVerticalStackContainerTileFragment on SDVerticalStackContainerTile {
    id
    tileType
    status
    tiles {
      id
      ...fullWidthTextComponentTileFragment
      ...TileServiceTileFragment
      ...LendingServiceTileFragment

      ... on SDHorizontalAdaptiveStackContainerTile {
        __typename
        id
        status
        tileType
        tiles {
          id
          ...TileServiceTileFragment
          ...LendingServiceTileFragment
        }
      }
    }
  }
  ${FullWidthTextComponentTileFragment}
  ${TileServiceTile}
  ${LendingServiceTile}
`;

export const HomeTile = gql`
  fragment homeTile on HomeTiles {
    __typename
    ...genericTile
    tiles {
      id
      ...genericTile
      ...cardTrackerTile
      ...internalAccountTile
      ...progressTrackerTile
      ...partnerOfferTile
      ...productOfferTile
      ...promotionTile
      ...buttonTile
      ...linkedAccountsTile
      ...nextBestActionTile
      ...creditScoreTile
      ...offerAdvanceTile
      ...sdListTile
      ...varoToAnyoneTile
      ...TileServiceTileFragment
      ...LendingServiceTileFragment
      ...SDVerticalStackContainerTileFragment
    }
  }
  ${CardTrackerTile}
  ${InternalAccountTile}
  ${PartnerOfferTile}
  ${ProductOfferTile}
  ${PromotionTile}
  ${ButtonTile}
  ${LinkedAccountsTile}
  ${GenericTile}
  ${NextBestActionTile}
  ${CreditScoreTile}
  ${AdvanceOfferTile}
  ${SdListTile}
  ${VaroToAnyoneTile}
  ${TileServiceTile}
  ${LendingServiceTile}
  ${ProgressTrackerTile}
  ${SdVerticalStackContainerTile}
`;

export const savingsTracker = gql`
  fragment savingsTracker on SavingsTracker {
    id
    title
    description
    lastUpdateTimeDescription
    metricUpdatedDescription
    banners {
      ...bannerMsgR2
    }
    requirements {
      id
      title
      description
      isEligible
      cta
      zendeskId
      type
      qualificationContentText
      iconColor
      requirementValue {
        ... on SavingsTrackerDirectDepositRequirement {
          customerValue {
            ...money
          }
          thresholdValue {
            ...money
          }
        }
      }
    }
  }
  ${money}
  ${bannerMsgR2}
`;

export const PerkSectionItemsFragment = gql`
  fragment PerkSectionItemsFragment on SectionItems {
    id
    title
    subTitle
    offerType
    cashback
    heroImage
    logoImage
    isNew
    backgroundColor
    merchantId
    typeId
    onClick {
      ... on SDPartnerReferralNavigationAction {
        __typename
        id
        analyticsEvent {
          ...analytics
        }
      }
      ... on SDPartnerIncentivesNavigationAction {
        __typename
        id
        analyticsEvent {
          ...analytics
        }
        resourceId
      }
      ... on SDPartnerOfferDetailNavigationAction {
        __typename
        id
        analyticsEvent {
          ...analytics
        }
        resourceId
      }
    }
  }
  ${AnalyticsFragment}
`;

export const PerkSectionItemsV2Fragment = gql`
  fragment PerkSectionItemsV2Fragment on SectionItems {
    id
    offerType
    merchantId
    offerSource
    title
    backgroundColor
    cashback
    distanceText
    heroImage
    isActivated
    isNew
    location {
      ...coordinate
    }
    logoImage
    offerStatus
    subTitle
    tag {
      ...tag
    }
    timeRemaining
    typeId
    ctaLabel
    ctaButton {
      id
      buttonText
      buttonStyle
      buttonOnClick {
        ... on ActivateOfferAction {
          __typename
          offerId
          analyticsEvent {
            ...analytics
          }
        }
      }
    }
    onClick {
      ... on SDPartnerReferralNavigationAction {
        __typename
        id
        analyticsEvent {
          ...analytics
        }
      }
      ... on SDPartnerIncentivesNavigationAction {
        __typename
        id
        analyticsEvent {
          ...analytics
        }
        resourceId
      }
      ... on SDPartnerOfferDetailNavigationAction {
        __typename
        id
        analyticsEvent {
          ...analytics
        }
        resourceId
        merchantId
        offerSource
      }
    }
  }
  ${AnalyticsFragment}
  ${coordinate}
  ${tag}
`;

export const VXLTextAccessoryHorizontalStackComponent = gql`
  fragment VXLTextAccessoryHorizontalStackComponent on VXLTextAccessoryHorizontalStackComponent {
    __typename
    id
    accessories {
      __typename
      title {
        __typename
        id
        text {
          ...VXLResponsiveTextFragment
        }
      }
      subtitle {
        __typename
        id
        text {
          ...VXLResponsiveTextFragment
        }
        textAlignment
        maxLines
      }
      id
      iconEdge
      icon {
        ...IconFragment
      }
    }
  }
  ${IconFragment}
  ${VXLResponsiveTextFragment}
`;

export const articles = gql`
  fragment articles on Articles {
    copy
    articleId
    article {
      id
      title
    }
  }
`;

export const TransactionRowFragment = gql`
  fragment TransactionRowFragment on TransactionRowComponent {
    id
    icon {
      ...IconFragment
    }
    primaryText {
      ...VXLResponsiveTextFragment
    }
    secondaryText {
      ...VXLResponsiveTextFragment
    }
    primaryDetail {
      ...VXLResponsiveTextFragment
    }
    secondaryDetail {
      ...VXLResponsiveTextFragment
    }
    callout {
      id
      anchorLocation
      children {
        ... on HtmlComponent {
          html
          id
        }
      }
    }
    onClick {
      ... on TransactionDetailsNavigationAction {
        id
        analyticsEvent {
          ...analytics
        }
        transaction {
          type
          id
          accountId
          ... on RdcTransaction {
            rdcId
          }
        }
      }
    }
    tags {
      ...tag
    }
    displayAnalytics {
      ...analytics
    }
  }
  ${VXLResponsiveTextFragment}
  ${IconFragment}
  ${AnalyticsFragment}
  ${tag}
`;

export const offerBadge = gql`
  fragment offerBadge on Badge {
    text
    color
    __typename
  }
`;

export const partnerOffer = gql`
  fragment partnerOffer on PartnerOffer {
    id
    name
    merchantId
    industry
    shortDescription
    logoImage
    cashbackDescription
    cashbackAmount
    offerType
    offerSource
    heroImage
    badge {
      ...offerBadge
    }
  }
  ${offerBadge}
`;

export const DetailsItem = gql`
  fragment DetailsItem on ITransactionDetailItem {
    key
    title
    description
    value
  }
`;

export const CashbackTransactionDetails = gql`
  fragment CashbackTransactionDetails on ITransactionDetailGroup {
    title
    items {
      ...DetailsItem
    }
  }
  ${DetailsItem}
`;

export const TransactionDetailsNavigationAction = gql`
  fragment TransactionDetailsNavigationAction on TransactionDetailsNavigationAction {
    id
    analyticsEvent {
      ...analytics
    }
    transaction {
      id
      accountId
      accountLink {
        id
        type
        product
        status
        institution {
          name
        }
        name
        nickname
        iconUrl
        accountNumberLast4
      }
      type
      status
      direction
      createdAt
      value {
        ...money
      }
      iconUrl
      description
      shortDescription
      category {
        name
      }
      disputeUrl
      details {
        title
        items {
          key
          title
          description
          value
        }
      }
      detailsR2 {
        title
      }
      canSeeDetails
      disclaimer
      caption
    }
  }
  ${AnalyticsFragment}
  ${money}
`;

export const TransferAccountItem = gql`
  fragment TransferAccountItem on Account {
    id
    nickname
    product
    type
    iconUrl
    accountNumberLast4
    institution {
      name
    }
  }
`;

export const SuspiciousCardTxnDetailsTileFragment = gql`
  fragment SuspiciousCardTxnDetailsTileFragment on SuspiciousCardTxnDetailsTile {
    id
    txnDate
    merchant
    txnAmount {
      formatted
    }
    cardLast4
    tileType
  }
`;

export const QuestionFragment = gql`
  fragment QuestionFragment on Question {
    questionId
    questionText
    options {
      value
      label
    }
  }
`;

export const SuspiciousCardTxnQuestionTileFragment = gql`
  fragment SuspiciousCardTxnQuestionTileFragment on SuspiciousCardTxnQuestionTile {
    id
    tileType
    question {
      ...QuestionFragment
    }
  }
  ${QuestionFragment}
`;

export const ActionFragment = gql`
  fragment ActionFragment on Action {
    id
    copy
    action
    link
    type
    analyticsEvent {
      ...analytics
    }
  }
  ${AnalyticsFragment}
`;

export const SuspiciousCardTxnQuestionActionTileFragment = gql`
  fragment SuspiciousCardTxnQuestionActionTileFragment on SuspiciousCardTxnQuestionActionTile {
    id
    title
    message
    html
    tileType
    addressSectionTitle
    addressSectionSubTitle
    addressTitle
    mailingAddress {
      addressLine1
      city
      state
      zip
    }
    ctaActions {
      ...ActionFragment
    }
  }
  ${ActionFragment}
`;

export const MoveMoneyActionFragment = gql`
  fragment MoveMoneyActionFragment on MoveMoneyAccountAction {
    __typename
    id
    copy
    action
    type
    sourceAccountId
    destinationAccountId
    analyticsEvent {
      ...analytics
    }
  }
  ${AnalyticsFragment}
`;

export const ActionResultFragment = gql`
  fragment ActionResultFragment on ActionResult {
    __typename
    ...ActionFragment
    ...cardAction
    ...MoveMoneyActionFragment
  }
  ${ActionFragment}
  ${cardAction}
  ${MoveMoneyActionFragment}
`;

export const IntentOptionFragment = gql`
  fragment IntentOptionFragment on IntentOption {
    id
    title
    icon
    iconV2
    tagDetail {
      ...tag
    }
    analyticsEvent {
      ...analytics
    }
    action {
      ...ActionResultFragment
    }
  }
  ${AnalyticsFragment}
  ${ActionResultFragment}
  ${tag}
`;

export const BestActionErrorFragment = gql`
  fragment BestActionErrorFragment on BestActionError {
    __typename
    message
    code
    title
  }
`;

export const BestActionIntentCaptureFragment = gql`
  fragment BestActionIntentCaptureFragment on BestActionIntentCapture {
    __typename
    id
    headline
    type
    options {
      id
      title
      ...IntentOptionFragment
    }
    actionButton {
      ...ActionResultFragment
    }
    skipButton {
      ...ActionResultFragment
    }
    analyticsEvent {
      ...analytics
    }
  }
  ${ActionFragment}
  ${ActionResultFragment}
  ${IntentOptionFragment}
  ${AnalyticsFragment}
`;

export const ContentListFragment = gql`
  fragment ContentListFragment on ContentList {
    __typename
    content
    itemType
  }
`;

export const CopyValueFragment = gql`
  fragment CopyValueFragment on CopyValue {
    __typename
    id
    text
  }
`;

export const HtmlTypeFragment = gql`
  fragment HtmlTypeFragment on HTMLType {
    __typename
    id
    html
  }
`;

export const PdfLinkFragment = gql`
  fragment PdfLinkFragment on PdfLink {
    __typename
    id
    copy
    link
    analytics {
      ...analytics
    }
  }
  ${AnalyticsFragment}
`;

export const InlineAlertFragment = gql`
  fragment InlineAlertFragment on InlineAlert {
    __typename
    id
    alertType
    title
    body
    icon
  }
`;

export const VXLIconTypeFragment = gql`
  fragment VXLIconTypeFragment on VXLIconType {
    __typename
    key
  }
`;

export const VxlIllustrationTypeFragment = gql`
  fragment VxlIllustrationTypeFragment on VXLIllustrationType {
    __typename
    key
  }
`;

export const VxlListItemFragment = gql`
  fragment VxlListItemFragment on VxlListItem {
    primaryText
    secondaryText
    primaryNumberOfLines
    lineFormat
    isDisabled
    showsChevron
    leadingAccessory {
      __typename
      ...VXLIconTypeFragment
      ... on UserAvatar {
        userAvatarAsset {
          __typename
          ... on CopyValue {
            text
          }
          ... on URLType {
            url
          }
        }
      }
    }
    trailingAccessory {
      __typename
      ...tag
      ...offerBadge
    }
    onPress {
      __typename
      route
    }
  }
  ${tag}
  ${offerBadge}
  ${VXLIconTypeFragment}
`;

export const ListItemGroupFragment = gql`
  fragment ListItemGroupFragment on ListItemGroup {
    id
    __typename
    items {
      ...VxlListItemFragment
    }
  }
  ${VxlListItemFragment}
`;

export const BodyContentResultFragment = gql`
  fragment BodyContentResultFragment on BodyContentResult {
    ...ContentListFragment
    ...CopyValueFragment
    ...InlineAlertFragment
    ...ListItemGroupFragment
    ...PdfLinkFragment
  }
  ${ContentListFragment}
  ${CopyValueFragment}
  ${InlineAlertFragment}
  ${ListItemGroupFragment}
  ${PdfLinkFragment}
`;

export const VxlAnimationTypeFragment = gql`
  fragment VxlAnimationTypeFragment on VXLAnimationType {
    __typename
    animationKey: key
  }
`;

export const VisualResultFragment = gql`
  fragment VisualResultFragment on VisualResult {
    ...VxlAnimationTypeFragment
    ...VxlIllustrationTypeFragment
  }
  ${VxlAnimationTypeFragment}
  ${VxlIllustrationTypeFragment}
`;

export const ActionScreenFragment = gql`
  fragment ActionScreenFragment on ActionScreen {
    __typename
    id
    primaryButton {
      ...ActionResultFragment
    }
    secondaryButton {
      ...ActionResultFragment
    }
    skipButton {
      ...ActionResultFragment
    }
    titleText
    titleTextStyle
    bodyContent {
      ...BodyContentResultFragment
    }
    visual {
      ...VisualResultFragment
    }
    analyticsEvent {
      ...analytics
    }
  }
  ${cardAction}
  ${ActionFragment}
  ${ActionResultFragment}
  ${MoveMoneyActionFragment}
  ${BodyContentResultFragment}
  ${VisualResultFragment}
  ${AnalyticsFragment}
`;

export const ProductIntroScreenFragment = gql`
  fragment ProductIntroScreenFragment on ProductIntroScreen {
    __typename
    id
    primaryButton {
      ...ActionResultFragment
    }
    secondaryButton {
      ...ActionResultFragment
    }
    skipButton {
      ...ActionResultFragment
    }
    bodyContent {
      ...BodyContentResultFragment
    }
    analyticsEvent {
      ...analytics
    }
    backgroundTexture
    eyebrowText
    headlineText
    headlineTextStyle
    bodyContent {
      ...BodyContentResultFragment
    }
    templateVisual: visual {
      ...VisualResultFragment
    }
  }
  ${BodyContentResultFragment}
  ${ActionResultFragment}
  ${VisualResultFragment}
  ${AnalyticsFragment}
`;

export const BestActionResultV2Fragment = gql`
  fragment BestActionResultV2Fragment on BestActionResultV2 {
    ...ActionScreenFragment
    ...ProductIntroScreenFragment
    ...BestActionIntentCaptureFragment
    ...BestActionErrorFragment
  }
  ${ActionScreenFragment}
  ${ProductIntroScreenFragment}
  ${BestActionIntentCaptureFragment}
  ${BestActionErrorFragment}
`;

export const ProductOfferCardFragment = gql`
  ${IllustrationStyleDictionary}
  ${AnalyticsFragment}
  fragment ProductOfferCardFragment on ProductOfferCard {
    __typename
    id
    titleText
    bodyText
    actionTextV2
    illustration {
      ...IllustrationStyleDictionaryFragment
    }
    onClickV2 {
      __typename
      ... on RouteNavigationAction {
        __typename
        route
        analyticsEvent {
          ...analytics
        }
      }
    }
    illustrationKey
    imageBackgroundColor
  }
`;

export const ExternalCardFragment = gql`
  fragment ExternalCardFragment on ExternalCard {
    address {
      ...address
    }
    id
    institution {
      name
      __typename
    }
    isDefault
    maskedCardNumber
    cardTitle
    name
    nickName
    removeExternalCardInfo {
      message
      title
      __typename
    }
    cardNetwork
    iconUrl
    status
  }
  ${address}
`;

export const tabaPayError = gql`
  fragment tabaPayError on TabaPayCustomError {
    tabaPayErrorTitle: title
    tabaPayErrorMessage: message
    statusCode
    displayAnalytics {
      ...analytics
    }
    actions {
      button {
        buttonText
        buttonStyle
        buttonOnClick {
          __typename
          ... on RouteNavigationAction {
            __typename
            route
            analyticsEvent {
              ...analytics
            }
          }
          ... on SDContinueClientFlowAction {
            __typename
            analyticsEvent {
              ...analytics
            }
          }
          ... on SDBackNavigationAction {
            __typename
            analyticsEvent {
              ...analytics
            }
          }
        }
      }
      __typename
    }
  }
  ${AnalyticsFragment}
`;

export const VerifyCardActionScreenFragment = gql`
  fragment verifyCardActionScreen on VerifyCardActionScreen {
    __typename
    title
    message
    image
    button {
      id
      buttonText
      buttonStyle
      buttonOnClick {
        ... on SDContinueClientFlowAction {
          __typename
          analyticsEvent {
            ...analytics
          }
        }
      }
    }
  }
  ${AnalyticsFragment}
`;

export const FlowStepFragment = gql`
  fragment FlowStepFragment on FlowStep {
    id
    currentStep
    stepIndex
    totalSteps
    nextStep
    canGoBack
    canSkip
    progress
    flowMetadata {
      __typename
      flowType
      version
      experimentId
    }
  }
`;

export const LaunchPinwheelFlowActionFragment = gql`
  fragment LaunchPinwheelFlowActionFragment on LaunchPinwheelFlowAction {
    __typename
    id
    shouldLaunchPinwheelSdk
    analyticsEvent {
      ...AnalyticsEventFragment
    }
  }
`;

export const GenericBottomSheetTakeoverFragment = gql`
  fragment GenericBottomSheetTakeoverFragment on GenericBottomSheetTakeover {
    __typename
    id
    type
    title
    body
    disclosure
    cta
  }
`;

export const RewardsPromoSectionFragment = gql`
  fragment RewardsPromoSectionFragment on PromoSectionData {
    id
    cardData {
      __typename
      bodyText
      actionText
      illustration
      colorTheme
      disabled
      accessibilityLabel
      dismissAccessibilityLabel
    }
    details {
      __typename
      id
      title
      body
      primaryButton {
        id
        buttonText
        buttonStyle
      }
    }
  }
`;

export const VendorItemFragment = gql`
  fragment VendorItemFragment on VendorItem {
    activatedDuration
    address {
      ...address
      externalId
    }
    id
    isActive
    location {
      ...coordinate
    }
    distanceFormatted
    cashbackValue
    cashbackRateFormatted
    tertiary
    logoUrl
    merchantId
    name
    label
    marker
    offerSource
    onClick {
      analyticsEvent {
        ...analytics
      }
      route
    }
    pricingTier {
      cashbackPerUnit
      disountedPrice {
        ...money
      }
      originalPrice {
        ...money
      }
      tierDisplayName
      tierType
    }
    tag {
      ...tag
    }
  }
  ${address}
  ${coordinate}
  ${money}
  ${tag}
  ${AnalyticsFragment}
`;
